import { useApiMutation, useApiQuery } from "@/hooks/use-api";
import { Content } from "@/models/document";
import { Tracker, TrackerCreate, TrackerImportCreate } from "@/models/tracker";
import { API_CROWLINGO_AUTHED, hasSelectedWorkspace } from "./core";

export const useCreateTracker = () =>
  useApiMutation<Tracker, TrackerCreate>({
    url: "trackers",
    method: "POST",
  });

export const useCreateTrackerImport = () =>
  useApiMutation<Tracker, TrackerImportCreate>({
    url: "trackers",
    method: "POST",
  });

export const useDeleteTracker = () =>
  useApiMutation<{}, {}, { id: string }>({
    url: `trackers/{id}`,
    method: "DELETE",
  });

export const useUpdateStatusTracker = () =>
  useApiMutation<Tracker, Partial<Tracker>, { id: string; tracker: Tracker }>({
    url: `trackers/{id}`,
    method: "PATCH",
  });

// API pour mettre à jour un tracker (params, cron, metadata)
export const useUpdateTracker = () =>
  useApiMutation<Tracker, Partial<Tracker>, { id: string }>({
    url: `trackers/{id}`,
    method: "PATCH",
  });

// API pour mettre à jour les tags d'un tracker
export const useUpdateTrackerTags = () =>
  useApiMutation<Tracker, { tags: string[] }, { id: string }>({
    url: `trackers/{id}`,
    method: "PATCH",
  });

// API pour mettre à jour le groupe d'un tracker
export const useUpdateTrackerGroup = () =>
  useApiMutation<Tracker, { group: { id: string } | null }, { id: string }>({
    url: `trackers/{id}`,
    method: "PATCH",
  });

export const useSearchTrackerQuery = (param: {
  endpoint: string;
  params: Record<string, unknown>;
  limit: number;
}) =>
  useApiQuery<Content[]>({
    url: `trackers/search`,
    method: "POST",
    data: param,
  });

// Nouvelle API pour exécuter une recherche directe sans sauvegarder
export const useRunTrackerSearch = (param: {
  source: string;
  tracker: string;
  query: string;
  limit?: number;
}) =>
  useApiQuery<Content[]>({
    url: `trackers/run`,
    method: "POST",
    data: {
      source: param.source,
      endpoint: `${param.source}_${param.tracker}`,
      params: {
        query: param.query,
      },
      limit: param.limit || 20,
    },
  });

// API pour exécuter une recherche avec l'endpoint /trackers/run/search
export const useRunTrackerSearchNew = (
  param: {
    source: string;
    tracker: string;
    trackerPath: string;
    params: Record<string, unknown>;
    limit?: number;
  },
  queryConfig?: { enabled?: boolean; staleTime?: number },
) => {
  const requestData: {
    source: string;
    path: string;
    params: Record<string, unknown>;
    limit?: number;
  } = {
    source: param.source,
    path: param.trackerPath,
    params: {
      ...param.params,
    },
  };

  // Ajouter limit seulement s'il est défini
  if (param.limit !== undefined) {
    requestData.limit = param.limit;
  }

  return useApiQuery<Content[]>(
    {
      url: `trackers/run/search`,
      method: "POST",
      data: requestData,
    },
    queryConfig,
  );
};

export const useTrackers = () =>
  useApiQuery<Tracker[]>(
    { url: `/trackers` },
    { enabled: hasSelectedWorkspace() },
  );

// API pour récupérer un seul tracker par son ID
export const useTracker = (id?: string) =>
  useApiQuery<Tracker>(
    { url: `/trackers/${id}` },
    { enabled: !!id && hasSelectedWorkspace() },
  );

/** Execution record from ES aggregation */
export interface TrackerExecution {
  /** Request ID (unique per execution batch) */
  id: string;
  /** Number of documents indexed in this execution */
  documentsFound: number;
  /** Earliest document insertion time */
  startTime: string;
  /** Latest document insertion time */
  endTime?: string;
  /** Status is always success for indexed documents */
  status: "success";
}

// API pour récupérer l'historique des exécutions d'un tracker
export const useTrackerExecutions = (trackerId?: string, limit: number = 20) =>
  useApiQuery<TrackerExecution[]>(
    { url: `/trackers/${trackerId}/executions?limit=${limit}` },
    { enabled: !!trackerId && hasSelectedWorkspace() },
  );

// API pour gérer les tags des trackers
export const useAddTagToTracker = () =>
  useApiMutation<{}, {}, { trackerId: string; tagId: string }>({
    url: `trackers/{trackerId}/tags/{tagId}`,
    method: "POST",
  });

export const useRemoveTagFromTracker = () =>
  useApiMutation<{}, {}, { trackerId: string; tagId: string }>({
    url: `trackers/{trackerId}/tags/{tagId}`,
    method: "DELETE",
  });

// Note: Les tags sont maintenant inclus dans la réponse du tracker via useTrackers()
// Cet endpoint n'est plus nécessaire

export const useDeleteTrackerDocuments = () =>
  useApiMutation<{ deleted: number }, {}, { id: string }>({
    url: `trackers/{id}/documents`,
    method: "DELETE",
  });

export const useImportTrackerDocuments = () =>
  useApiMutation<{}, { _: unknown[] }, { trackerId: string }>({
    url: `trackers/{trackerId}/import_documents`,
    method: "POST",
  });

/** Options for running a tracker */
export interface RunTrackerOptions {
  /** Maximum number of results to fetch */
  limit?: number;
  /** Time period for the search (in days) */
  periodDays?: number;
  /** Custom start date (ISO format) */
  fromDate?: string;
  /** Custom end date (ISO format) */
  toDate?: string;
  /** Force execution even if recently run */
  force?: boolean;
  /** Run asynchronously */
  runAsync?: boolean;
}

// Helper function to build query params string from options
export const buildRunTrackerQueryString = (
  options?: RunTrackerOptions,
): string => {
  const params = new URLSearchParams();

  // Force defaults to True
  params.set("force", options?.force !== false ? "True" : "False");

  // Run async defaults to False
  params.set("run_async", options?.runAsync ? "True" : "False");

  if (options?.limit !== undefined) {
    params.set("limit", String(options.limit));
  }

  if (options?.periodDays !== undefined) {
    params.set("period_days", String(options.periodDays));
  }

  if (options?.fromDate) {
    params.set("from_date", options.fromDate);
  }

  if (options?.toDate) {
    params.set("to_date", options.toDate);
  }

  return params.toString();
};

// API pour exécuter un tracker par son ID avec options
// Uses a factory function to include query params in the URL
export const useRunTrackerWithOptions = (options?: RunTrackerOptions) => {
  const queryString = buildRunTrackerQueryString(options);
  return useApiMutation<{ status: string }, {}, { tracker_id: string }>({
    url: `trackers/{tracker_id}/run_monitoring?${queryString}`,
    method: "POST",
  });
};

// Simple version for backward compatibility (with default options)
export const useRunTracker = () =>
  useApiMutation<{ status: string }, {}, { tracker_id: string }>({
    url: `trackers/{tracker_id}/run_monitoring?force=True&run_async=False`,
    method: "POST",
  });

// Direct API call for running tracker with dynamic options (doesn't use hook)
export const runTrackerWithOptions = async (
  trackerId: string,
  options?: RunTrackerOptions,
) => {
  const queryString = buildRunTrackerQueryString(options);
  return API_CROWLINGO_AUTHED.post<{ status: string }>(
    `trackers/${trackerId}/run_monitoring?${queryString}`,
  );
};

// export const runTracker = (tracker: Tracker) =>
// 	API_CROWLINGO_AUTHED.post<Document[]>("trackers/tracker/run", { ...tracker })

export const getSuggestions =
  (abortSignal: AbortSignal) => (param: { value: string; endpoint: string }) =>
    API_CROWLINGO_AUTHED.post<{ id: string; name: string }[]>(
      "trackers/run",
      {
        status: "active",
        endpoint: param.endpoint,
        params: {
          query: param.value,
        },
        limit: 10,
      },
      { signal: abortSignal },
    );
